<template>
	<vs-sidebar
		position-right
		class="items-no-padding"
		parent="#chat-app"
		default-index="1"
		color="primary"
		spacer
		v-model="isSettingsSidebarActive"
	>
		<div class="mt-4 flex items-center justify-end px-6">
			<feather-icon
				icon="XIcon"
				@click.stop="$emit('closeSettingsSidebarEmit')"
				class="cursor-pointer"
			></feather-icon>
		</div>
		<vx-card no-shadow>
			<vs-avatar
				v-if="chat.admin"
				class="mx-auto mb-4 block"
				icon-pack="feather"
				icon="icon-users"
				color="primary"
				size="64px"
				:src="null"
			/>
			<vs-avatar v-if="!chat.admin" class="mx-auto mb-4 block" :src="null" size="64px" />
			<div class="text-center">
				<h5>{{ chat.name }}</h5>
			</div>
			<div class="flex justify-between flex-wrap mt-4">
				<vs-button
					v-show="chat.admin !== userInfoId && !isDefaultChat(chat.key)"
					icon-pack="feather"
					color="danger"
					type="flat"
					icon="icon-log-out"
					class="w-full mt-1"
					@click="openConfirmLeave(userInfoId)"
					>{{ $t("Chat.LeaveGroup") }}
				</vs-button>
			</div>
			<template slot="footer">
				<div class="flex justify-between">
					<span class="flex items-center">
						<feather-icon icon="icon-user" svgClasses="h-5 w-5 text-warning stroke-current" />
						<span class="ml-2">{{ members.length }} {{ $t("Chat.Members") }}</span>
					</span>
					<span class="flex items-center" v-if="chat.admin === userInfoId">
						<vs-button radius icon-pack="feather" icon="icon-user-plus" @click="popupGroupInfo = true" />
					</span>
				</div>
			</template>
		</vx-card>
		<vs-divider v-show="chat.admin" class="m-0"></vs-divider>
		<vue-perfect-scrollbar class="scroll-area-two" :settings="settings" @ps-scroll-y="scrollHandle">
			<vx-card no-shadow>
				<ul class="friend-suggesions-list">
					<li
						class="friend-suggestion flex items-center mb-4"
						v-for="(member, index) in members"
						:key="index"
					>
						<div class="mr-3">
							<vs-avatar
								class="m-0"
								:src="contactImage(member.id) ? contactImage(member.id).img : null"
								size="35px"
							/>
						</div>
						<div class="leading-tight">
							<p class="font-medium">{{ member.username | capitalize }}</p>
							<span class="text-xs">{{ member.role_name }}</span>
						</div>
						<div class="ml-auto cursor-pointer" v-show="chat.admin">
							<vs-button
								radius
								type="border"
								color="danger"
								icon-pack="feather"
								icon="icon-trash"
								v-show="chat.admin === userInfoId"
								@click="openConfirmDelete(member)"
							/>
						</div>
					</li>
				</ul>
			</vx-card>
		</vue-perfect-scrollbar>
		<vs-prompt
			:title="$t('UpdateGroup')"
			class="export-options"
			@cancel="closePopUp"
			@accept="updateChatGroup"
			:accept-text="$t('Crud.Update')"
			:cancel-text="$t('Cancel')"
			@close="closePopUp"
			:is-valid="groupSelectedMembers.length > 0 && groupName !== ''"
			:active.sync="popupGroupInfo"
		>
			<label class="vs-input--label">{{ $t("Name") }}</label>
			<vs-input v-model="groupName" class="w-full mb-4" />
			<label class="vs-input--label"> {{ $t("Add") }} {{ $t("Chat.Members") }}</label>
			<treeselect
				value-consists-of="LEAF_PRIORITY"
				:default-expand-level="1"
				:placeholder="$t('Username')"
				:clearable="false"
				:multiple="true"
				:options="contacts"
				v-model="groupSelectedMembers"
				:max-height="300"
				:noResultsText="$t('Chat.NoContactFound')"
				class="mb-base"
			/>
		</vs-prompt>
	</vs-sidebar>
</template>

<script>
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import Chat from "@/models/Chat.js";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";

export default {
	components: {
		VuePerfectScrollbar,
		Treeselect,
	},
	props: {
		members: {
			type: Array,
			default: () => [],
		},
		userInfoId: {
			type: Number,
			default: 0,
		},
		chat: {
			type: Chat,
			default: () => new Chat({}),
		},
		isSettingsSidebarActive: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			settings: {
				maxScrollbarLength: 60,
				wheelSpeed: 0.7,
			},
			popupGroupInfo: false,
			groupName: "",
			groupSelectedMembers: [],
		};
	},
	computed: {
		contactImage() {
			return function (id) {
				return this.$store.getters["chat/contactImg"](+id);
			};
		},
		contacts() {
			const contacts = this.$store.getters["chat/contacts"];
			let usersContacts = contacts.map((resource) => {
				return {id: resource.id, label: resource.username};
			});
			return usersContacts;
		},
		isDefaultChat() {
			return (key) => key && key.split("_")[0] === "group";
		},
	},
	methods: {
		openLoading() {
			this.$vs.loading();
		},
		closeLoading() {
			this.$vs.loading.close();
		},
		scrollHandle(evt) {},
		openConfirmLeave(id) {
			this.$vs.dialog({
				type: "confirm",
				color: "danger",
				title: this.$t("Confirm"),
				text: this.$t("Chat.LeaveGroupText"),
				acceptText: this.$t("Accept"),
				cancelText: this.$t("Cancel"),
				accept: () => this.removeMember(id, true),
			});
		},
		openConfirmDelete(member) {
			this.$vs.dialog({
				type: "confirm",
				color: "danger",
				title: this.$t("Confirm"),
				text: `${this.$t("Chat.DeleteMemberText")} ${member.username}?`,
				acceptText: this.$t("Accept"),
				cancelText: this.$t("Cancel"),
				accept: () => this.removeMember(member.id),
			});
		},
		async removeMember(id, close = false) {
			const payload = {
				memberId: id,
				chatKey: this.chat.key,
			};
			await this.$store.dispatch("chat/removeMemberFromGroup", payload);
			this.$emit("removeMemberEmit", id);
			if (close) this.$emit("closeSettingsSidebarEmit");
		},
		closePopUp() {},
		async updateChatGroup() {
			const payload = {
				members: this.groupSelectedMembers,
				name: this.groupName,
				chatKey: this.chat.key,
			};
			try {
				this.openLoading();
				await this.$store.dispatch("chat/updateGroupChatRoom", payload);
				this.$emit("updateMembersEmit", this.groupSelectedMembers);
				this.$vs.notify({
					color: "success",
					title: this.$t("TitleConfirmGroupUpdate"),
					text: this.$t("TextConfirmGroupUpdate"),
				});
				this.closeLoading();
			} catch (error) {
				this.closeLoading();
				this.$vs.notify({
					color: "danger",
					title: this.$t("ErrorTitle"),
					text: this.$t("ErrorMessage"),
				});
			}
		},
	},
	watch: {
		popupGroupInfo(val) {
			if (val) {
				this.groupSelectedMembers = this.members.map((m) => m.id);
				this.groupName = this.chat.name;
			}
		},
	},
};
</script>

<style lang="css" scoped>
.scroll-area-two {
	margin: auto;
	width: auto;
	min-height: 200px;
	max-height: 350px;
}
</style>
